
import React from "react";
import { useTranslations } from "use-intl";
import { useFormatters } from "@financialharmony/ui";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { BankAccountsStatus, Invoice, InvoiceStatus, StatusBadge } from "@/types/payments";
import PaymentDateButton from "./PaymentDateButton";
import InvoiceActionMenu from "./InvoiceActionMenu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import SekEquivalent from "@/components/shared/SekEquivalent";
import PaymentVerificationBadge from "@/components/shared/PaymentVerificationBadge";
import { useTaxPayableCaption } from "@/i18n/useTaxTransactionLabel";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";

interface InvoiceTableRowProps {
  invoice: Invoice;
  showCheckboxes: boolean;
  toggleSelectInvoice: (invoiceId: string) => void;
  selectedInvoices: string[];
  showPaymentDate: boolean;
  updatePaymentDate?: (invoiceId: string, date: Date) => void;
  getStatusBadge: (status: InvoiceStatus) => StatusBadge;
  formatCurrency: (amount: number, currency: string) => string;
  onRowClick: (invoice: Invoice) => void;
  disablePastDates: (date: Date) => boolean;
  getPaymentDateObject: (invoice: Invoice) => Date;
  isLatePayment: (dueDate: string, paymentDate: string) => boolean;
  isHolidayAdjusted?: (invoice: Invoice) => boolean;
  onPay?: (invoiceId: string, paymentDate: Date, actualSekAmount?: number) => void;
  onCancel?: (invoiceId: string) => void;
  onExport?: (invoiceId: string) => void;
  onDownload?: (invoiceId: string, fileName: string) => void;
  onEditPayment?: (invoiceId: string) => void;
  onConfirmMatch?: (payableId: string, transactionId: string) => Promise<boolean>;
  /** Pass-through — see BankAccountsStatus's docstring (#498 whole-branch review finding I2). */
  hasBankAccounts?: BankAccountsStatus;
}

// Utility component for truncating text with hover functionality
const TruncatedCell = ({ content }: { content: string }) => {
  if (!content || content === "-") return <span>-</span>;
  
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <div className="max-w-[200px] truncate cursor-help">{content}</div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 text-sm">
        {content}
      </HoverCardContent>
    </HoverCard>
  );
};

const InvoiceTableRow: React.FC<InvoiceTableRowProps> = ({
  invoice,
  showCheckboxes,
  toggleSelectInvoice,
  selectedInvoices,
  updatePaymentDate,
  getStatusBadge,
  formatCurrency,
  onRowClick,
  disablePastDates,
  getPaymentDateObject,
  isLatePayment,
  isHolidayAdjusted,
  onPay,
  onCancel,
  onExport,
  onDownload,
  onEditPayment,
  onConfirmMatch,
  hasBankAccounts
}) => {
  const t = useTranslations("consultantPayables.table");
  const { formatDate } = useFormatters();
  const formatOptionalDate = useFormatOptionalDate();
  const taxCaption = useTaxPayableCaption();
  const status = getStatusBadge(invoice.status);
  // Default payment date to due date if not set
  const paymentDate = invoice.payablePaymentDate || invoice.paymentDate || invoice.dueDate;
  const isLate = isLatePayment(invoice.dueDate, paymentDate);
  const isDateAdjusted = isHolidayAdjusted ? isHolidayAdjusted(invoice) : false;
  
  // Only show editable payment date for unpaid/overdue invoices
  const showEditablePaymentDate = invoice.status === "unpaid" || invoice.status === "overdue";

  return (
    <TableRow 
      id={`invoice-${invoice.id}`}
      key={invoice.id}
      className="cursor-pointer hover:bg-muted/60 transition-colors"
      onClick={() => onRowClick(invoice)}
    >
      {showCheckboxes && (
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox 
            checked={selectedInvoices.includes(invoice.id)} 
            onCheckedChange={() => toggleSelectInvoice(invoice.id)} 
            aria-label={t("selectInvoice", { id: invoice.id })} 
          />
        </TableCell>
      )}
      <TableCell>
        <Badge variant={status?.variant}>{status?.label}</Badge>
        <PaymentVerificationBadge paymentVerification={invoice.paymentVerification} />
      </TableCell>
      <TableCell>
        <TruncatedCell content={invoice.vendor} />
        {invoice.tax && (
          <Badge variant="outline" className="mt-1">
            {taxCaption(invoice.tax.transactionType, invoice.tax.description)}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <TruncatedCell content={invoice.remittanceInformation} />
      </TableCell>
      <TableCell>
        <span className="font-mono text-sm">{invoice.sourceAccountNumber || '-'}</span>
      </TableCell>
      <TableCell>
        {formatCurrency(invoice.amount, invoice.currency)}
        <SekEquivalent sekAmount={invoice.sekAmount} currency={invoice.currency} />
      </TableCell>
      <TableCell>{formatOptionalDate(invoice.dueDate)}</TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {showEditablePaymentDate && updatePaymentDate ? (
          <PaymentDateButton 
            paymentDate={paymentDate}
            dueDate={invoice.dueDate}
            isLate={isLate}
            isHolidayAdjusted={isDateAdjusted}
            updatePaymentDate={(date) => updatePaymentDate(invoice.id, date)}
            disablePastDates={disablePastDates}
            getPaymentDateObject={() => getPaymentDateObject(invoice)}
          />
        ) : (
          <span>
            {paymentDate ? formatDate(paymentDate, "numeric") : "-"}
          </span>
        )}
      </TableCell>
      <TableCell>
        <InvoiceActionMenu 
          invoice={invoice}
          onView={onRowClick}
          onPay={onPay}
          onCancel={onCancel}
          onExport={onExport}
          onDownload={onDownload}
          onEditPayment={onEditPayment}
          onConfirmMatch={onConfirmMatch}
          hasBankAccounts={hasBankAccounts}
        />
      </TableCell>
    </TableRow>
  );
};

export default InvoiceTableRow;
